"use client";
import 'isomorphic-fetch';
import {
  ApolloLink,
  HttpLink,
  split,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import {
  ApolloNextAppProvider,
  ApolloClient,
  InMemoryCache,
  SSRMultipartLink,
} from "@apollo/experimental-nextjs-app-support";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client"; // Import the createUploadLink
import { getToken } from "./utils";
import React from 'react'
import serverSocketLink from "./wsLink";
import { getMainDefinition } from "@apollo/client/utilities";
import { clearAuthStorage } from "./auth";


const api_url = process.env.NEXT_PUBLIC_API_ENDPOINT!;

function makeClient() {
  const apolloAuthContext = setContext((_, { headers }) => {
    const jwt_token = getToken()
   
    return {
      headers: {
        ...headers,
        Authorization: jwt_token ? `Bearer ${jwt_token}` : "",
      },
    };
  });

  // Error handling link for unauthorized/invalid token responses
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        const status = err?.extensions?.code;
        
        // Check for 401 (Unauthorized) or 403 (Forbidden) errors
        if (status === 'UNAUTHENTICATED' || status === 'FORBIDDEN' || err.message?.includes('Unauthorized') || err.message?.includes('Invalid') || err.message?.includes('expired')) {
          console.warn("Invalid or expired token detected, clearing storage");
          clearAuthStorage();
        }
      }
    }

    if (networkError) {
      // Handle HTTP errors like 401, 403
      const statusCode = (networkError as any).statusCode;
      if (statusCode === 401 || statusCode === 403) {
        console.warn("Unauthorized access detected, clearing storage");
        clearAuthStorage();
      }
    }
  });
  const uploadLink = createUploadLink({
    uri: api_url,
  });

  const splitLink = (typeof window !== 'undefined' && window !== null)
    ? split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
            );
        },
        serverSocketLink || uploadLink, // Use serverSocketLink if available, otherwise uploadLink
        uploadLink
    )
    : uploadLink;
  

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            myField: {
              merge(existing, incoming) {
                return { ...existing, ...incoming };
              }
            }
          }
        }
      }
    }),
    link: ApolloLink.from([
      errorLink,
      apolloAuthContext,
      new SSRMultipartLink({
        stripDefer: true,
      }),
      splitLink
    ]),
    defaultOptions: {
      watchQuery: {
        errorPolicy: "all",
      },
    },
  });
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  return (
    <ApolloNextAppProvider
      makeClient={makeClient}
    >
      {children}
    </ApolloNextAppProvider>
  );
}
